import React, { useEffect, useMemo, useState } from "react";
import { X, FileText, Edit3, Send, CheckCircle2, ThumbsUp, PlayCircle,
         RefreshCw, AlertTriangle, FileWarning, Link2, ChevronRight,
         GitCompareArrows, Eye, Download, Printer } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { RevisionDiffDialog } from "./RevisionDiffDialog";
import { previewPdf as previewPdfBlob, downloadPdf as downloadPdfBlob, printPdf as printPdfBlob } from "../lib/pdfActions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─────────────────────────────────────────────────────────
// Static labels (10 sections — matches backend SECTIONS)
// ─────────────────────────────────────────────────────────
const SECTION_LABELS = {
  purpose:          "Amaç",
  scope:            "Kapsam",
  responsibilities: "Sorumluluklar",
  definitions:      "Tanımlar / Kısaltmalar",
  references:       "Referans Dokümanlar",
  prerequisites:    "Ön Koşullar / Hazırlık",
  procedure_steps:  "Uygulama / Prosedür",
  safety:           "İş Sağlığı ve Güvenlik",
  records:          "İlgili Kayıtlar / Formlar",
  revision_notes:   "Revizyon Notları",
};
const SECTION_ORDER = Object.keys(SECTION_LABELS);

const STATUS_BADGE = {
  DRAFT:      { label: "Taslak",       cls: "bg-slate-100 text-slate-700" },
  IN_REVIEW:  { label: "İnceleme",     cls: "bg-blue-100 text-blue-700" },
  CHECKED:    { label: "Kontrol Edildi", cls: "bg-indigo-100 text-indigo-700" },
  APPROVED:   { label: "Onaylandı",    cls: "bg-emerald-100 text-emerald-700" },
  ACTIVE:     { label: "Yürürlükte",   cls: "bg-emerald-600 text-white" },
  SUPERSEDED: { label: "Süperseded",   cls: "bg-slate-300 text-slate-700" },
  REJECTED:   { label: "Reddedildi",   cls: "bg-rose-100 text-rose-700" },
  REQUIRED:   { label: "Gerekli",      cls: "bg-red-100 text-red-700 border border-red-300" },
};

const GAP_STYLE = {
  REQUIRED:  "text-red-700 bg-red-50",
  DRAFT:     "text-orange-700 bg-orange-50",
  IN_REVIEW: "text-orange-700 bg-orange-50",
  CHECKED:   "text-yellow-800 bg-yellow-50",
  APPROVED:  "text-emerald-700 bg-emerald-50",
  ACTIVE:    "text-emerald-800 bg-emerald-100",
};

const authHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});


// ═════════════════════════════════════════════════════════
// Controlled Content Center
// ═════════════════════════════════════════════════════════
export const ControlledContentCenter = ({ open, onClose, openPdfPreview }) => {
  const [tab, setTab] = useState("matrix"); // matrix | list
  const [matrix, setMatrix] = useState([]);
  const [areas, setAreas] = useState([]);
  const [filterArea, setFilterArea] = useState("");
  const [filterKind, setFilterKind] = useState("");
  const [filterGap, setFilterGap] = useState("");
  const [contentList, setContentList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // content doc under edit

  const loadMatrix = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/gmp/controlled-content/discovery-matrix`, authHeader());
      setMatrix(r.data.entries || []);
      setAreas(r.data.areas || []);
    } catch (e) {
      toast.error("Discovery matrix yüklenemedi");
    } finally { setLoading(false); }
  };

  const loadList = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/gmp/controlled-content`, authHeader());
      setContentList(r.data || []);
    } catch (e) {
      toast.error("İçerik listesi yüklenemedi");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!open) return;
    loadMatrix();
    loadList();
  }, [open]);

  const createDraft = async (scope) => {
    try {
      const r = await axios.post(`${API}/gmp/controlled-content`,
        { process_scope: scope }, authHeader());
      toast.success(`Taslak oluşturuldu: ${r.data.master_document_code_snapshot}`);
      await loadMatrix(); await loadList();
      setEditing(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Taslak oluşturulamadı");
    }
  };

  const openContent = async (id) => {
    try {
      const r = await axios.get(`${API}/gmp/controlled-content/${id}`, authHeader());
      setEditing(r.data);
    } catch (e) {
      toast.error("İçerik açılamadı");
    }
  };

  const filteredMatrix = useMemo(() => matrix.filter(e => {
    if (filterArea && e.area !== filterArea) return false;
    if (filterKind && e.kind !== filterKind) return false;
    if (filterGap && e.gap_status !== filterGap) return false;
    return true;
  }), [matrix, filterArea, filterKind, filterGap]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
         data-testid="cc-modal">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-7xl max-h-[92vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-3 border-b bg-slate-50">
          <div className="flex items-center gap-3">
            <FileText className="w-5 h-5 text-slate-700" />
            <h2 className="text-lg font-semibold">Kontrollü İçerikler</h2>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-200 rounded"
                  data-testid="cc-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="border-b flex items-center gap-1 px-4 bg-white">
          {[
            { k: "matrix", label: "Discovery Matrix" },
            { k: "list", label: "İçerik Listesi" },
          ].map(t => (
            <button key={t.k}
                    onClick={() => setTab(t.k)}
                    data-testid={t.k === "matrix" ? "cc-tab-matrix" : "cc-tab-list"}
                    className={`px-4 py-2 text-sm border-b-2 ${
                      tab === t.k ? "border-emerald-600 text-emerald-700 font-medium"
                                   : "border-transparent text-slate-600 hover:text-slate-900"
                    }`}>
              {t.label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-auto p-6">
          {tab === "matrix" && (
            <MatrixTab
              entries={filteredMatrix}
              areas={areas}
              filterArea={filterArea} setFilterArea={setFilterArea}
              filterKind={filterKind} setFilterKind={setFilterKind}
              filterGap={filterGap} setFilterGap={setFilterGap}
              onCreate={createDraft}
              onOpen={openContent}
              loading={loading}
            />
          )}
          {tab === "list" && (
            <ListTab rows={contentList} onOpen={openContent} loading={loading} />
          )}
        </div>
      </div>

      {editing && (
        <ContentEditor
          content={editing}
          onClose={() => setEditing(null)}
          onSaved={async (id) => {
            await loadMatrix(); await loadList();
            if (id) await openContent(id);
          }}
          openPdfPreview={openPdfPreview}
        />
      )}
    </div>
  );
};


// ═════════════════════════════════════════════════════════
// Matrix tab
// ═════════════════════════════════════════════════════════
const MatrixTab = ({ entries, areas, filterArea, setFilterArea,
                     filterKind, setFilterKind, filterGap, setFilterGap,
                     onCreate, onOpen, loading }) => (
  <div className="space-y-3">
    <div className="flex flex-wrap gap-2 items-center text-sm">
      <select value={filterArea} onChange={e => setFilterArea(e.target.value)}
              data-testid="cc-filter-area"
              className="border rounded px-2 py-1">
        <option value="">Tüm Alanlar</option>
        {areas.map(a => <option key={a} value={a}>{a}</option>)}
      </select>
      <select value={filterKind} onChange={e => setFilterKind(e.target.value)}
              data-testid="cc-filter-kind"
              className="border rounded px-2 py-1">
        <option value="">Tüm Türler</option>
        <option value="SOP">SOP</option>
        <option value="INSTRUCTION">Talimat (T)</option>
        <option value="GENERAL">Kontrollü Doküman (D)</option>
      </select>
      <select value={filterGap} onChange={e => setFilterGap(e.target.value)}
              data-testid="cc-filter-gap"
              className="border rounded px-2 py-1">
        <option value="">Tüm Durumlar</option>
        <option value="REQUIRED">Eksik (Gerekli)</option>
        <option value="DRAFT">Taslak</option>
        <option value="IN_REVIEW">İnceleme</option>
        <option value="CHECKED">Kontrol Edildi</option>
        <option value="APPROVED">Onaylandı</option>
        <option value="ACTIVE">Yürürlükte</option>
      </select>
      <span className="text-xs text-slate-500 ml-auto">
        {entries.length} kapsam listeleniyor
      </span>
    </div>

    <div className="border rounded overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-slate-100 text-slate-700">
          <tr>
            <th className="px-3 py-2 text-left">Alan</th>
            <th className="px-3 py-2 text-left">Proses Kapsamı</th>
            <th className="px-3 py-2 text-left">Tür</th>
            <th className="px-3 py-2 text-left">Önerilen Doküman</th>
            <th className="px-3 py-2 text-left">Master Kod</th>
            <th className="px-3 py-2 text-left">Durum</th>
            <th className="px-3 py-2 text-left">Eksik Bilgi</th>
            <th className="px-3 py-2"></th>
          </tr>
        </thead>
        <tbody>
          {entries.map(e => {
            const gap = e.gap_status || "REQUIRED";
            const style = GAP_STYLE[gap] || "text-slate-700";
            return (
              <tr key={e.scope} className="border-t hover:bg-slate-50">
                <td className="px-3 py-2 text-xs font-medium">{e.area}</td>
                <td className="px-3 py-2 text-xs font-mono">{e.scope}</td>
                <td className="px-3 py-2 text-xs">{e.kind}</td>
                <td className="px-3 py-2">{e.title}</td>
                <td className="px-3 py-2 text-xs font-mono">
                  {e.master_document_code || <span className="text-slate-400">—</span>}
                </td>
                <td className="px-3 py-2">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${style}`}>
                    {(STATUS_BADGE[gap] || STATUS_BADGE.REQUIRED).label}
                  </span>
                </td>
                <td className="px-3 py-2 text-xs">
                  {e.pending_confirmations > 0 && (
                    <span className="inline-flex items-center gap-1 text-amber-700">
                      <AlertTriangle className="w-3 h-3" />
                      {e.pending_confirmations}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-right">
                  <div className="inline-flex gap-1 items-center justify-end">
                    {e.controlled_content_id ? (
                      <>
                        <button
                          onClick={() => previewPdfBlob(`/api/gmp/controlled-content/${e.controlled_content_id}/pdf`, `${e.master_document_code || "Kontrollu"}.pdf`)}
                          title="PDF Önizle"
                          data-testid={`cc-preview-${e.scope}`}
                          className="inline-flex items-center justify-center w-7 h-7 rounded bg-indigo-600 hover:bg-indigo-700 text-white">
                          <Eye className="w-3.5 h-3.5"/>
                        </button>
                        <button
                          onClick={() => downloadPdfBlob(`/api/gmp/controlled-content/${e.controlled_content_id}/pdf`, `${e.master_document_code || "Kontrollu"}.pdf`)}
                          title="PDF İndir"
                          data-testid={`cc-download-${e.scope}`}
                          className="inline-flex items-center justify-center w-7 h-7 rounded bg-slate-100 hover:bg-slate-200 border text-slate-800">
                          <Download className="w-3.5 h-3.5"/>
                        </button>
                        <button
                          onClick={() => printPdfBlob(`/api/gmp/controlled-content/${e.controlled_content_id}/pdf`, `${e.master_document_code || "Kontrollu"}.pdf`)}
                          title="PDF Yazdır"
                          data-testid={`cc-print-${e.scope}`}
                          className="inline-flex items-center justify-center w-7 h-7 rounded bg-slate-100 hover:bg-slate-200 border text-slate-800">
                          <Printer className="w-3.5 h-3.5"/>
                        </button>
                        <button onClick={() => onOpen(e.controlled_content_id)}
                                data-testid={`cc-open-${e.scope}`}
                                className="text-xs text-blue-700 hover:underline ml-1">
                          Aç
                        </button>
                      </>
                    ) : (
                      <button onClick={() => onCreate(e.scope)}
                              data-testid={`cc-create-${e.scope}`}
                              className="text-xs px-2 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700">
                        Taslak Oluştur
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </div>
);


// ═════════════════════════════════════════════════════════
// List tab
// ═════════════════════════════════════════════════════════
const ListTab = ({ rows, onOpen, loading }) => (
  <div className="border rounded overflow-hidden">
    <table className="w-full text-sm">
      <thead className="bg-slate-100 text-slate-700">
        <tr>
          <th className="px-3 py-2 text-left">Doküman Kodu</th>
          <th className="px-3 py-2 text-left">Ad</th>
          <th className="px-3 py-2 text-left">Tür</th>
          <th className="px-3 py-2 text-left">Alan</th>
          <th className="px-3 py-2 text-left">Durum</th>
          <th className="px-3 py-2 text-left">Rev.</th>
          <th className="px-3 py-2 text-left">Eksik</th>
          <th className="px-3 py-2"></th>
        </tr>
      </thead>
      <tbody>
        {rows.map(r => {
          const pending = (r.confirmation_items || []).filter(i => i.status === "PENDING").length;
          const badge = STATUS_BADGE[r.status] || STATUS_BADGE.DRAFT;
          return (
            <tr key={r.id} className="border-t hover:bg-slate-50">
              <td className="px-3 py-2 text-xs font-mono">
                {r.master_document_code_snapshot || "—"}
              </td>
              <td className="px-3 py-2">{r.title}</td>
              <td className="px-3 py-2 text-xs">{r.doc_kind}</td>
              <td className="px-3 py-2 text-xs">{r.area}</td>
              <td className="px-3 py-2">
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${badge.cls}`}>
                  {badge.label}
                </span>
              </td>
              <td className="px-3 py-2 text-xs">{r.revision_no}</td>
              <td className="px-3 py-2 text-xs">
                {pending > 0 && <span className="text-amber-700">{pending}</span>}
              </td>
              <td className="px-3 py-2 text-right">
                <div className="inline-flex gap-1 items-center justify-end">
                  <button
                    onClick={() => previewPdfBlob(`/api/gmp/controlled-content/${r.id}/pdf`, `${r.master_document_code_snapshot || "Kontrollu"}.pdf`)}
                    title="PDF Önizle"
                    data-testid={`cc-list-preview-${r.id.slice(0,8)}`}
                    className="inline-flex items-center justify-center w-7 h-7 rounded bg-indigo-600 hover:bg-indigo-700 text-white">
                    <Eye className="w-3.5 h-3.5"/>
                  </button>
                  <button
                    onClick={() => downloadPdfBlob(`/api/gmp/controlled-content/${r.id}/pdf`, `${r.master_document_code_snapshot || "Kontrollu"}.pdf`)}
                    title="PDF İndir"
                    data-testid={`cc-list-download-${r.id.slice(0,8)}`}
                    className="inline-flex items-center justify-center w-7 h-7 rounded bg-slate-100 hover:bg-slate-200 border text-slate-800">
                    <Download className="w-3.5 h-3.5"/>
                  </button>
                  <button
                    onClick={() => printPdfBlob(`/api/gmp/controlled-content/${r.id}/pdf`, `${r.master_document_code_snapshot || "Kontrollu"}.pdf`)}
                    title="PDF Yazdır"
                    data-testid={`cc-list-print-${r.id.slice(0,8)}`}
                    className="inline-flex items-center justify-center w-7 h-7 rounded bg-slate-100 hover:bg-slate-200 border text-slate-800">
                    <Printer className="w-3.5 h-3.5"/>
                  </button>
                  <button onClick={() => onOpen(r.id)}
                          data-testid={`cc-list-open-${r.id.slice(0,8)}`}
                          className="text-xs text-blue-700 hover:underline ml-1">
                    Aç <ChevronRight className="w-3 h-3 inline" />
                  </button>
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);


// ═════════════════════════════════════════════════════════
// Content Editor (structured sections + confirmation panel + workflow)
// ═════════════════════════════════════════════════════════
const ContentEditor = ({ content, onClose, onSaved, openPdfPreview }) => {
  const [sections, setSections] = useState(content.sections || {});
  const [saving, setSaving] = useState(false);
  const [confItems, setConfItems] = useState(content.confirmation_items || []);
  const [showDiff, setShowDiff] = useState(false);
  const cid = content.id;
  const status = content.status;
  const canEdit = ["DRAFT", "IN_REVIEW"].includes(status);
  const pending = confItems.filter(i => i.status === "PENDING").length;

  const setSection = (key, val) => setSections(s => ({ ...s, [key]: val }));

  const saveSections = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/gmp/controlled-content/${cid}`,
        { sections }, authHeader());
      toast.success("Kaydedildi");
      await onSaved(cid);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kaydedilemedi");
    } finally { setSaving(false); }
  };

  const transition = async (target) => {
    try {
      await axios.post(`${API}/gmp/controlled-content/${cid}/transition`,
        { target }, authHeader());
      toast.success(`Durum: ${target}`);
      await onSaved(cid);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Durum değiştirilemedi");
    }
  };

  const newRevision = async () => {
    const reason = window.prompt("Revizyon gerekçesi:");
    if (!reason) return;
    try {
      const r = await axios.post(`${API}/gmp/controlled-content/${cid}/revision`,
        { reason }, authHeader());
      toast.success(`Yeni revizyon: ${r.data.master_document_code_snapshot}`);
      await onSaved(r.data.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Revizyon açılamadı");
    }
  };

  const previewPdfAction = () => previewPdfBlob(
    `/api/gmp/controlled-content/${cid}/pdf`,
    `${content.master_document_code_snapshot || "Kontrollu"}.pdf`,
  );
  const downloadPdfAction = () => downloadPdfBlob(
    `/api/gmp/controlled-content/${cid}/pdf`,
    `${content.master_document_code_snapshot || "Kontrollu"}.pdf`,
  );
  const printPdfAction = () => printPdfBlob(
    `/api/gmp/controlled-content/${cid}/pdf`,
    `${content.master_document_code_snapshot || "Kontrollu"}.pdf`,
  );

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4"
         data-testid="cc-editor">
      <div className="bg-white rounded-lg w-full max-w-6xl max-h-[92vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-3 border-b bg-emerald-50">
          <div>
            <h3 className="text-base font-semibold">{content.title}</h3>
            <div className="text-xs text-slate-600 mt-1">
              <span className="font-mono">{content.master_document_code_snapshot || "—"}</span>
              <span className="mx-2">•</span>
              {content.area} / {content.doc_kind} / Rev {content.revision_no}
              <span className={`ml-3 px-2 py-0.5 rounded text-xs font-medium ${(STATUS_BADGE[status] || STATUS_BADGE.DRAFT).cls}`}>
                {(STATUS_BADGE[status] || STATUS_BADGE.DRAFT).label}
              </span>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-200 rounded"
                  data-testid="cc-editor-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 overflow-auto p-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Left: Sections */}
          <div className="lg:col-span-2 space-y-3">
            {SECTION_ORDER.map(key => (
              <div key={key} className="border rounded">
                <div className="px-3 py-2 bg-slate-50 border-b flex items-center justify-between">
                  <span className="text-sm font-medium">{SECTION_LABELS[key]}</span>
                  <span className="text-[10px] text-slate-500 uppercase">ERP WORKFLOW</span>
                </div>
                <textarea
                  data-testid={`cc-section-${key}`}
                  value={sections[key] || ""}
                  onChange={e => setSection(key, e.target.value)}
                  disabled={!canEdit}
                  rows={4}
                  placeholder={`${SECTION_LABELS[key]} için içerik yazın (bilinmeyen teknik değerler için Doğrulanması Gereken Bilgiler paneline soru ekleyin).`}
                  className={`w-full p-3 text-sm resize-y focus:outline-none ${canEdit ? "" : "bg-slate-50 text-slate-500"}`}
                />
              </div>
            ))}
            {canEdit && (
              <button onClick={saveSections} disabled={saving}
                      data-testid="cc-save-sections"
                      className="px-4 py-2 rounded bg-slate-800 text-white text-sm hover:bg-slate-900">
                {saving ? "Kaydediliyor..." : "Bölümleri Kaydet"}
              </button>
            )}
          </div>

          {/* Right: Confirmations + Actions */}
          <div className="space-y-4">
            <ConfirmationPanel
              cid={cid}
              items={confItems}
              canEdit={canEdit}
              canApprove={["CHECKED","IN_REVIEW"].includes(status) || canEdit}
              onChange={async () => {
                const r = await axios.get(`${API}/gmp/controlled-content/${cid}`, authHeader());
                setConfItems(r.data.confirmation_items || []);
              }} />

            <div className="border rounded p-3 space-y-2">
              <div className="text-xs font-semibold text-slate-700 mb-2">Doküman İşlemleri</div>
              <div className="grid grid-cols-3 gap-1">
                <button onClick={previewPdfAction}
                        data-testid="cc-pdf-preview"
                        className="text-xs px-2 py-2 rounded bg-indigo-600 hover:bg-indigo-700 text-white flex items-center justify-center gap-1">
                  <Eye className="w-3 h-3" /> Önizle
                </button>
                <button onClick={downloadPdfAction}
                        data-testid="cc-pdf-download"
                        className="text-xs px-2 py-2 rounded border hover:bg-slate-50 flex items-center justify-center gap-1">
                  <Download className="w-3 h-3" /> İndir
                </button>
                <button onClick={printPdfAction}
                        data-testid="cc-pdf-print"
                        className="text-xs px-2 py-2 rounded border hover:bg-slate-50 flex items-center justify-center gap-1">
                  <Printer className="w-3 h-3" /> Yazdır
                </button>
              </div>
              {status === "DRAFT" && (
                <button onClick={() => transition("IN_REVIEW")}
                        data-testid="cc-send-review"
                        className="w-full text-left text-sm px-3 py-2 rounded border hover:bg-slate-50 flex items-center gap-2">
                  <Send className="w-4 h-4" /> İncelemeye Gönder
                </button>
              )}
              {status === "IN_REVIEW" && (
                <button onClick={() => transition("CHECKED")}
                        data-testid="cc-check"
                        className="w-full text-left text-sm px-3 py-2 rounded border hover:bg-slate-50 flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" /> Kontrol Et
                </button>
              )}
              {status === "CHECKED" && (
                <button onClick={() => transition("APPROVED")}
                        data-testid="cc-approve"
                        disabled={pending > 0}
                        title={pending > 0 ? `${pending} bekleyen doğrulama var` : ""}
                        className={`w-full text-left text-sm px-3 py-2 rounded border flex items-center gap-2 ${pending > 0 ? "opacity-40 cursor-not-allowed" : "hover:bg-slate-50"}`}>
                  <ThumbsUp className="w-4 h-4" /> Onayla
                  {pending > 0 && <span className="ml-auto text-xs text-amber-700">{pending} eksik</span>}
                </button>
              )}
              {status === "APPROVED" && (
                <button onClick={() => transition("ACTIVE")}
                        data-testid="cc-activate"
                        className="w-full text-left text-sm px-3 py-2 rounded bg-emerald-600 text-white hover:bg-emerald-700 flex items-center gap-2">
                  <PlayCircle className="w-4 h-4" /> Aktifleştir
                </button>
              )}
              {status === "ACTIVE" && (
                <button onClick={newRevision}
                        data-testid="cc-new-revision"
                        className="w-full text-left text-sm px-3 py-2 rounded border hover:bg-slate-50 flex items-center gap-2">
                  <RefreshCw className="w-4 h-4" /> Yeni Revizyon
                </button>
              )}
              <button onClick={() => setShowDiff(true)}
                      data-testid="cc-revision-diff"
                      className="w-full text-left text-sm px-3 py-2 rounded border hover:bg-indigo-50 flex items-center gap-2">
                <GitCompareArrows className="w-4 h-4 text-indigo-700" /> Revizyon Karşılaştır
              </button>
            </div>
          </div>
        </div>
      </div>
      <RevisionDiffDialog open={showDiff} currentId={cid}
                           onClose={() => setShowDiff(false)} />
    </div>
  );
};


// ═════════════════════════════════════════════════════════
// Confirmation Panel (Doğrulanması Gereken Bilgiler)
// ═════════════════════════════════════════════════════════
const ConfirmationPanel = ({ cid, items, canEdit, canApprove, onChange }) => {
  const [values, setValues] = useState({});
  const pending = items.filter(i => i.status === "PENDING");
  const done = items.filter(i => i.status !== "PENDING");

  const confirm = async (itemId) => {
    const v = (values[itemId] || "").trim();
    if (!v) { toast.error("Değer boş olamaz"); return; }
    try {
      await axios.patch(`${API}/gmp/controlled-content/${cid}/confirmations/${itemId}`,
        { status: "CONFIRMED", confirmed_value: v }, authHeader());
      toast.success("Doğrulandı");
      onChange && onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Doğrulanamadı");
    }
  };

  const reject = async (itemId) => {
    const reason = window.prompt("Ret gerekçesi:");
    if (!reason) return;
    try {
      await axios.patch(`${API}/gmp/controlled-content/${cid}/confirmations/${itemId}`,
        { status: "REJECTED", rejection_reason: reason }, authHeader());
      toast.success("Reddedildi");
      onChange && onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reddedilemedi");
    }
  };

  return (
    <div className="border rounded overflow-hidden" data-testid="cc-confirmation-panel">
      <div className="px-3 py-2 bg-amber-50 border-b flex items-center gap-2">
        <AlertTriangle className="w-4 h-4 text-amber-700" />
        <span className="text-xs font-semibold text-amber-800">
          Doğrulanması Gereken Bilgiler ({pending.length})
        </span>
      </div>
      {items.length === 0 && (
        <div className="p-3 text-xs text-slate-500">Bekleyen doğrulama yok.</div>
      )}
      <div className="divide-y">
        {pending.map(it => (
          <div key={it.id} className="p-3 space-y-2 bg-amber-50/40">
            <div>
              <div className="text-sm font-medium">{it.field_label}
                {it.unit && <span className="ml-1 text-xs text-slate-500">[{it.unit}]</span>}
              </div>
              {it.description && (
                <div className="text-xs text-slate-600 mt-0.5">{it.description}</div>
              )}
              <div className="text-[10px] text-slate-400 mt-0.5 uppercase">
                {SECTION_LABELS[it.section_key] || it.section_key}
              </div>
            </div>
            {canApprove && (
              <div className="flex gap-2">
                <input value={values[it.id] || ""} onChange={e => setValues(v => ({...v, [it.id]: e.target.value}))}
                       data-testid={`cc-confirm-input-${it.id.slice(0,8)}`}
                       placeholder="Onaylanan değer"
                       className="flex-1 border rounded px-2 py-1 text-xs" />
                <button onClick={() => confirm(it.id)}
                        data-testid="cc-confirm-save"
                        className="px-2 py-1 rounded bg-emerald-600 text-white text-xs">Kaydet</button>
                <button onClick={() => reject(it.id)}
                        data-testid={`cc-confirm-reject-${it.id.slice(0,8)}`}
                        className="px-2 py-1 rounded border text-xs text-rose-700">Ret</button>
              </div>
            )}
          </div>
        ))}
        {done.map(it => (
          <div key={it.id} className="p-3 bg-emerald-50/30">
            <div className="text-xs font-medium">{it.field_label}</div>
            {it.status === "CONFIRMED" ? (
              <div className="text-xs text-emerald-800 mt-1">
                ✓ {it.confirmed_value}
                <span className="ml-2 text-[10px] text-slate-500">
                  ({it.confirmed_by_name} — {(it.confirmed_at || "").slice(0,10)})
                </span>
              </div>
            ) : (
              <div className="text-xs text-rose-700 mt-1">
                ✗ Red: {it.rejection_reason}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ControlledContentCenter;
